// CustomTextField.tsx
import React, { useState } from "react";
import { IconButton, Link, makeStyles } from "@material-ui/core";
import classNames from "classnames";
import { Box } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { Eye, EyeOff, LucideIcon } from "lucide-react";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    "& input": {
      width: "100%",
    },
    paddingBottom: theme.spacing(2),
    "@media (max-width: 450px)": {
      paddingBottom: theme.spacing(1),
      "& input": {
        fontSize: "0.875rem",
      },
    },
    position: "relative",
  },
  label: {
    fontSize: "0.775rem",
    fontWeight: 500,
  },
  input: {
    width: "100%",
    marginTop: theme.spacing(0.5),

    padding: theme.spacing(1.25, 2),
    fontSize: "0.775rem", // text-sm
    [theme.breakpoints.up("sm")]: {
      fontSize: "1rem", // text-base
      padding: theme.spacing(1.5, 2.5),
    },
    "@media (max-width: 450px)": {
      fontSize: "0.675rem", // text-base
      padding: theme.spacing(0.8, 1.75),
      marginTop: theme.spacing(0.25),
    },
    borderRadius: theme.shape.borderRadius * 3,
  },
  inputIcon: { paddingLeft: "35px !important" },
  error: {
    color: theme.palette.error.main,
  },
  borderError: {
    border: `1px solid ${theme.palette.error.main}`,
    "&:focus": {
      outline: "none",
      boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
    },
  },
  borderNormal: {
    border: "1px solid #e5e7eb;", // ~ border-gray-300
    "&:focus": {
      outline: "none",
      border: `1px solid ${theme.palette.primary.light}`,
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
      // ~ focus:ring-2 focus:ring-primary/20
    },
  },
  iconEye: {
    width: 17,
    height: 17,
  },
  iconLeft: {
    position: "absolute",
    left: 12,
    transform: "translateY(-50%)",
    width: 17,
    height: 17,
    color: "#9ca3af",
    "@media (max-width: 450px)": {
      width: 15,
      height: 15,
    },
  },
  iconNormal: {
    top: "53%",
    "@media (max-width: 450px)": {
      top: "62%",
    },
  },
  iconHasError: {
    top: "45%",
    "@media (max-width: 450px)": {
      top: "46%",
    },
  },
  link: {
    cursor: "pointer",
    textAlign: "right",
    textDecoration: "inherit",
    "&.hover": {
      textDecoration: "underline",
    },
    "@media (max-width: 768px)": {
      textDecoration: "underline",
    },
  },
  labelContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.25),
    "@media (max-width: 450px)": {
      marginBottom: theme.spacing(0),
    },
  },
  endAdornment: {
    position: "absolute",
    right: 5,
    top: "55%",
    transform: "translateY(-50%)",
    pointerEvents: "none", // afin que l'adornment ne capture pas les clics
    "@media (max-width: 450px)": {
      top: "60%",
      right: 3,
    },
  },
  iconPassword: {
    position: "absolute",
    right: 12,
    top: "55%",
    transform: "translateY(-50%)",
    padding: 0,
    "@media (max-width: 450px)": {
      top: "60%",
    },
  },
}));

export interface ICustomTextFieldProps {
  name: string;
  label?: string | React.ReactNode;
  value?: string | ReadonlyArray<string> | number | undefined;
  placeholder?: string;
  isSubmitted?: boolean;
  displayError?: boolean;
  errorMessageId?: string;
  type?:
    | "button"
    | "checkbox"
    | "color"
    | "date"
    | "datetime-local"
    | "email"
    | "file"
    | "hidden"
    | "image"
    | "month"
    | "number"
    | "password"
    | "radio"
    | "range"
    | "reset"
    | "search"
    | "submit"
    | "tel"
    | "text"
    | "time"
    | "url"
    | "week";
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  iconLeft?: LucideIcon;
  link?: {
    label: string;
    onClick: () => void;
  };
  disabled?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  endAdornment?: React.ReactNode;
}

const CustomTextField = (props: ICustomTextFieldProps) => {
  const classes = useStyles();
  const [showPassword, setShowPassword] = useState(false);
  const {
    name,
    label,
    placeholder,
    onChange,
    value,
    type = "text",
    iconLeft: IconLeft,
    link,
    disabled,
    inputProps, // nouvelle prop pour les attributs de l'input
    endAdornment, // nouvelle prop pour l'adornment
  } = props;

  const isSubmitted = !!props.isSubmitted;
  const displayError = !!props.displayError;
  const errorMessageId = props.errorMessageId || "error.notEmptyField";
  const hasError = (isSubmitted && !value) || (isSubmitted && displayError);
  const isPasswordField = type === "password";

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box className={classes.root}>
      {label && (
        <Box className={classes.labelContent}>
          <span
            className={classNames(classes.label, hasError && classes.error)}
          >
            {label}
          </span>
          {link?.label && (
            <Link className={classes.link} onClick={link.onClick}>
              {link.label}
            </Link>
          )}
        </Box>
      )}
      {IconLeft && (
        <IconLeft
          className={classNames(
            classes.iconLeft,
            hasError ? classes.iconHasError : classes.iconNormal,
          )}
        />
      )}
      <input
        name={name}
        type={isPasswordField && showPassword ? "text" : type}
        placeholder={
          isPasswordField && !placeholder ? "**********" : placeholder
        }
        value={value}
        onChange={onChange}
        className={classNames(
          classes.input,
          hasError ? classes.borderError : classes.borderNormal,
          IconLeft && classes.inputIcon,
        )}
        // On applique ici les attributs supplémentaires (min, step, max, etc.)
        {...inputProps}
        // Si vous souhaitez réserver de l'espace pour un adornment, vous pouvez aussi ajouter un paddingRight ici
        style={{
          ...((inputProps && inputProps.style) || {}),
          paddingRight: isPasswordField ? 40 : undefined,
        }}
        disabled={disabled}
      />
      {/* Affichage de l'adornment passé en prop */}
      {endAdornment && (
        <div className={classes.endAdornment}>{endAdornment}</div>
      )}
      {isPasswordField && value && (
        <IconButton
          onClick={handleTogglePasswordVisibility}
          className={classes.iconPassword}
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeOff className={classes.iconEye} />
          ) : (
            <Eye className={classes.iconEye} />
          )}
        </IconButton>
      )}
      {hasError && !value && (
        <span className={classes.error}>
          <FormattedMessage id={errorMessageId} />
        </span>
      )}
    </Box>
  );
};

export default CustomTextField;
